/**
 * WorkflowsTab — custom workflow templates + running-instance dashboard for DMS.
 *
 * - Manage templates (per entity_type: submittal/transmittal/rfi/review)
 * - Parallel + sequential stages
 * - Dashboard: in-progress / overdue / by entity type
 * - Instance detail modal with history
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Workflow, Layers, Clock, AlertTriangle, Trash2, GitBranch, Users, Send, RotateCw, Library } from 'lucide-react';
import api from '@/lib/api';
import { PermissionGate } from '@/components/PermissionGate';
import WorkflowDiagram from '@/components/dms/WorkflowDiagram';
import TemplateWizard from '@/components/dms/TemplateWizard';
import TemplateLibraryDialog from '@/components/dms/TemplateLibraryDialog';

const surfacePanel = 'bg-white dark:bg-gradient-to-br dark:from-[#0c1222] dark:to-[#0a0f1e] border border-slate-200 dark:border-slate-700/50 rounded-xl';
const subPanel     = 'bg-slate-50 dark:bg-[#0a0f1e] border border-slate-200 dark:border-slate-700/40 rounded-lg';

export default function WorkflowsTab({ projectId }) {
  const [inner, setInner] = useState('dashboard');

  return (
    <div className={`${surfacePanel} p-4`}>
      <Tabs value={inner} onValueChange={setInner}>
        <TabsList className="bg-slate-100 dark:bg-[#0c1222] p-0.5 mb-3">
          <TabsTrigger value="dashboard" className="text-xs" data-testid="wf-tab-dashboard"><Layers className="w-3.5 h-3.5 mr-1.5" /> Dashboard</TabsTrigger>
          <TabsTrigger value="templates" className="text-xs" data-testid="wf-tab-templates"><Workflow className="w-3.5 h-3.5 mr-1.5" /> Templates</TabsTrigger>
          <TabsTrigger value="instances" className="text-xs" data-testid="wf-tab-instances"><GitBranch className="w-3.5 h-3.5 mr-1.5" /> Instances</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard"><WorkflowDashboard projectId={projectId} /></TabsContent>
        <TabsContent value="templates"><TemplatesManager projectId={projectId} /></TabsContent>
        <TabsContent value="instances"><InstancesList projectId={projectId} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DASHBOARD
// ─────────────────────────────────────────────────────────────
function WorkflowDashboard({ projectId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/dms/projects/${projectId}/workflows/dashboard`);
      setData(r.data);
    } catch { toast.error('Dashboard load failed'); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const migrateLegacy = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/dms/projects/${projectId}/workflows/migrate-legacy-submittals`);
      toast.success(`Migrated ${r.data.migrated} legacy submittal(s)`);
      load();
    } catch { toast.error('Migration failed'); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="text-xs text-slate-500 p-4">Loading…</div>;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatTile label="Total" value={data.total_instances} />
        <StatTile label="In Progress" value={data.by_status.IN_PROGRESS || 0} tone="amber" />
        <StatTile label="Completed"   value={data.by_status.COMPLETED   || 0} tone="emerald" />
        <StatTile label="Overdue"     value={data.overdue.length}              tone="red" icon={AlertTriangle} />
        <StatTile label="Due Soon"    value={data.upcoming.length}             tone="sky"  icon={Clock} />
      </div>

      <div className="flex items-center justify-end">
        <PermissionGate module="DMS" action="import">
          <Button size="sm" variant="outline" onClick={migrateLegacy} disabled={busy} data-testid="wf-migrate-legacy">
            <RotateCw className="w-3.5 h-3.5 mr-1" /> Migrate Legacy Submittals
          </Button>
        </PermissionGate>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <OverdueTable title="Overdue" rows={data.overdue} tone="red" />
        <OverdueTable title="Due Within 48h" rows={data.upcoming} tone="amber" />
      </div>

      <div className={`${subPanel} p-3`}>
        <div className="text-[10px] uppercase text-slate-500 mb-2">By Entity Type</div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(data.by_entity).map(([k, v]) => (
            <span key={k} className="text-xs px-2 py-1 rounded-full bg-sky-100 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300">
              {k.toUpperCase()}: {v}
            </span>
          ))}
          {Object.keys(data.by_entity).length === 0 && <span className="text-xs text-slate-500">No instances yet.</span>}
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value, tone = 'slate', icon: Icon }) {
  const cls = {
    slate:   'text-slate-800 dark:text-slate-200',
    amber:   'text-amber-600 dark:text-amber-300 border-l-4 border-l-amber-500',
    emerald: 'text-emerald-600 dark:text-emerald-300 border-l-4 border-l-emerald-500',
    red:     'text-red-600 dark:text-red-300 border-l-4 border-l-red-500',
    sky:     'text-sky-600 dark:text-sky-300 border-l-4 border-l-sky-500',
  }[tone];
  return (
    <div className={`${subPanel} p-3 ${tone !== 'slate' ? cls.split(' ').filter(c => c.startsWith('border')).join(' ') : ''}`}>
      <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1">{Icon && <Icon className="w-3 h-3" />} {label}</div>
      <div className={`text-2xl font-bold ${cls.split(' ').filter(c => !c.startsWith('border')).join(' ')}`}>{value}</div>
    </div>
  );
}

function OverdueTable({ title, rows, tone }) {
  const border = { red: 'border-l-red-500', amber: 'border-l-amber-500' }[tone] || 'border-l-slate-500';
  return (
    <div className={`${subPanel} border-l-4 ${border} p-3 overflow-hidden`}>
      <div className="text-xs font-semibold mb-2">{title} ({rows.length})</div>
      {rows.length === 0 ? <div className="text-xs text-slate-500">Nothing here — all clear.</div> : (
        <div className="max-h-64 overflow-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-500">
              <tr>
                <th className="p-1 text-left">Entity</th>
                <th className="p-1 text-left">Stage</th>
                <th className="p-1 text-right">Hours</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 20).map(r => (
                <tr key={r.instance_id} className="border-t border-slate-200 dark:border-slate-700/30">
                  <td className="p-1"><span className="text-slate-500 uppercase mr-1">{r.entity_type}</span> {r.entity_label || r.entity_id.slice(0, 8)}</td>
                  <td className="p-1">{r.stage_name}</td>
                  <td className={`p-1 text-right font-mono ${r.hours_remaining < 0 ? 'text-red-500' : 'text-amber-500'}`}>{r.hours_remaining}h</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TEMPLATES MANAGER
// ─────────────────────────────────────────────────────────────
function TemplatesManager({ projectId }) {
  const [tpls, setTpls] = useState([]);
  const [showEdit, setShowEdit] = useState(null);   // null | 'new' | template obj
  const [showLibrary, setShowLibrary] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/dms/projects/${projectId}/workflows/templates`);
      setTpls(r.data);
    } catch { /* silent */ }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await api.delete(`/dms/projects/${projectId}/workflows/templates/${tpl.id}`);
      toast.success('Template deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-500">{tpls.length} template(s)</div>
        <div className="flex items-center gap-2">
          <PermissionGate module="DMS" action="configure">
            <Button size="sm" variant="outline" className="h-8" onClick={() => setShowLibrary(true)} data-testid="wf-tpl-library">
              <Library className="w-3.5 h-3.5 mr-1" /> Install from Library
            </Button>
            <Button size="sm" className="bg-[#1E4E8C] text-white h-8" onClick={() => setShowEdit('new')} data-testid="wf-tpl-new">
              <Plus className="w-3.5 h-3.5 mr-1" /> New Template
            </Button>
          </PermissionGate>
        </div>
      </div>

      <div className="grid gap-3">
        {tpls.length === 0 ? <div className="text-xs text-slate-500 p-4">No templates yet.</div> : tpls.map(t => (
          <div key={t.id} className={`${subPanel} p-3`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">{t.name}</span>
                {t.is_default && <span className="text-[9px] bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 px-2 py-0.5 rounded-full">DEFAULT</span>}
                <span className="text-[9px] bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300 px-2 py-0.5 rounded-full uppercase">{t.entity_type}</span>
              </div>
              <div className="flex items-center gap-1">
                <PermissionGate module="DMS" action="configure">
                  <button
                    onClick={async () => {
                      try {
                        await api.post(`/dms/projects/${projectId}/workflows/templates/${t.id}/publish`);
                        toast.success('Published to marketplace');
                      } catch (e) { toast.error(e?.response?.data?.detail || 'Publish failed'); }
                    }}
                    className="text-xs text-fuchsia-500 hover:underline"
                    title="Publish to project-shared marketplace"
                    data-testid={`wf-tpl-publish-${t.name}`}
                  >Publish</button>
                  <button onClick={() => setShowEdit(t)} className="text-xs text-sky-500 hover:underline" data-testid={`wf-tpl-edit-${t.name}`}>Edit</button>
                  <button onClick={() => remove(t)} className="text-xs text-red-500 hover:underline"><Trash2 className="w-3 h-3" /></button>
                </PermissionGate>
              </div>
            </div>
            {t.description && <div className="text-[10px] text-slate-500 mb-2">{t.description}</div>}
            <WorkflowDiagram stages={t.stages} compact />
          </div>
        ))}
      </div>

      <Dialog open={showEdit !== null} onOpenChange={(o) => !o && setShowEdit(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-auto">
          <DialogHeader><DialogTitle>{showEdit === 'new' ? 'New' : 'Edit'} Workflow Template</DialogTitle></DialogHeader>
          {showEdit !== null && (
            <TemplateWizard
              projectId={projectId}
              initial={showEdit === 'new' ? null : showEdit}
              onSaved={() => { setShowEdit(null); load(); }}
              onCancel={() => setShowEdit(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <TemplateLibraryDialog
        projectId={projectId}
        open={showLibrary}
        onClose={() => setShowLibrary(false)}
        onInstalled={() => load()}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// INSTANCES LIST
// ─────────────────────────────────────────────────────────────
function InstancesList({ projectId }) {
  const [rows, setRows] = useState([]);
  const [entityType, setEntityType] = useState('');
  const [status, setStatus] = useState('');
  const [detail, setDetail] = useState(null);
  const [actForm, setActForm] = useState({ action: 'APPROVE', comments: '' });

  const load = useCallback(async () => {
    const params = {};
    if (entityType) params.entity_type = entityType;
    if (status) params.status = status;
    try {
      const r = await api.get(`/dms/projects/${projectId}/workflows/instances`, { params });
      setRows(r.data);
    } catch { /* silent */ }
  }, [projectId, entityType, status]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (inst) => {
    const r = await api.get(`/dms/projects/${projectId}/workflows/instances/${inst.id}`);
    setDetail(r.data);
    setActForm({ action: (r.data.template_snapshot.stages[r.data.current_stage_index]?.allowed_actions?.[0]) || 'APPROVE', comments: '' });
  };

  const act = async () => {
    try {
      const r = await api.post(`/dms/projects/${projectId}/workflows/instances/${detail.id}/act`, actForm);
      toast.success(`Action recorded — now ${r.data.status}`);
      setDetail(r.data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Select value={entityType || 'all'} onValueChange={(v) => setEntityType(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40 h-8 text-xs"><SelectValue placeholder="Entity" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All entities</SelectItem>{['submittal', 'transmittal', 'rfi', 'review'].map(v => <SelectItem key={v} value={v}>{v.toUpperCase()}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status || 'all'} onValueChange={(v) => setStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40 h-8 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{['IN_PROGRESS', 'COMPLETED', 'REJECTED', 'REVISE_REQUIRED', 'CANCELLED'].map(v => <SelectItem key={v} value={v}>{v.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className={`${subPanel} overflow-hidden`}>
        <div className="max-h-[560px] overflow-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 dark:bg-slate-800/40 sticky top-0">
              <tr>
                <th className="p-2 text-left text-slate-500">Entity</th>
                <th className="p-2 text-left text-slate-500">Label</th>
                <th className="p-2 text-left text-slate-500">Template</th>
                <th className="p-2 text-left text-slate-500">Current Stage</th>
                <th className="p-2 text-left text-slate-500">Status</th>
                <th className="p-2 text-slate-500"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-slate-500">No workflow instances yet.</td></tr> : rows.map(r => {
                const total = r.template_snapshot.stages.length;
                const isTerminal = ['COMPLETED', 'REJECTED', 'CANCELLED'].includes(r.status);
                const idx = isTerminal ? total - 1 : Math.min(r.current_stage_index, total - 1);
                const stage = r.template_snapshot.stages[idx];
                const stageLabel = isTerminal
                  ? (r.status === 'COMPLETED' ? 'Completed' : r.status.replace('_', ' '))
                  : (stage?.name || '—');
                const progress = isTerminal ? `${total}/${total}` : `${idx + 1}/${total}`;
                return (
                  <tr key={r.id} className="border-t border-slate-200 dark:border-slate-700/30">
                    <td className="p-2 uppercase text-slate-500">{r.entity_type}</td>
                    <td className="p-2 text-slate-800 dark:text-slate-200">{r.entity_label || r.entity_id.slice(0, 12)}</td>
                    <td className="p-2 text-slate-600">{r.template_snapshot.name}</td>
                    <td className="p-2">{stageLabel} <span className="text-slate-400">({progress})</span></td>
                    <td className="p-2"><StatusChip status={r.status} /></td>
                    <td className="p-2 text-right"><Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => openDetail(r)} data-testid={`wf-inst-open-${r.entity_label || r.entity_id.slice(0, 6)}`}>Open</Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          {detail && <InstanceDetail detail={detail} actForm={actForm} setActForm={setActForm} act={act} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusChip({ status }) {
  const cls = {
    IN_PROGRESS:      'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
    COMPLETED:        'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300',
    REJECTED:         'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',
    REVISE_REQUIRED:  'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300',
    CANCELLED:        'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
  }[status] || 'bg-slate-100 text-slate-500';
  return <span className={`text-[10px] px-2 py-0.5 rounded-full ${cls}`}>{status.replace(/_/g, ' ')}</span>;
}

function InstanceDetail({ detail, actForm, setActForm, act }) {
  const [focusStage, setFocusStage] = useState(detail.current_stage_index);
  const focused = detail.template_snapshot.stages[focusStage] || detail.template_snapshot.stages[detail.current_stage_index];
  const focusedState = detail.stage_state[String(focusStage)] || { approvals: [] };
  const isCurrent = focusStage === detail.current_stage_index && detail.status === 'IN_PROGRESS';
  return (
    <div>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {detail.entity_type.toUpperCase()} <span className="font-mono text-fuchsia-500">{detail.entity_label || detail.entity_id}</span>
          <StatusChip status={detail.status} />
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-3 py-2 text-xs">
        <div className={`${subPanel} p-3`}>
          <div className="text-[10px] uppercase text-slate-500 mb-2 flex items-center gap-1"><GitBranch className="w-3 h-3" /> Workflow · <span className="text-fuchsia-500 normal-case">click any stage to focus its approvers</span></div>
          <WorkflowDiagram
            stages={detail.template_snapshot.stages}
            currentStageIndex={detail.current_stage_index}
            focusStageIndex={focusStage}
            stageState={detail.stage_state}
            status={detail.status}
            onStageClick={setFocusStage}
          />
        </div>

        <div className={`${subPanel} p-3 ${isCurrent ? 'border-l-4 border-l-sky-500' : 'border-l-4 border-l-slate-400 dark:border-l-slate-600'}`} data-testid={`wf-stage-focus-${focusStage}`}>
          <div className={`font-semibold ${isCurrent ? 'text-sky-700 dark:text-sky-300' : 'text-slate-700 dark:text-slate-300'} mb-1`}>
            Stage {focusStage + 1}: {focused?.name} <span className="text-[10px] font-normal text-slate-500">({focused?.mode})</span>
          </div>
          {focusedState.due_at && <div className="text-[10px] text-slate-500 mb-2">Due: {new Date(focusedState.due_at).toLocaleString()}</div>}
          <div className="text-[10px] text-slate-500 mb-2">
            Reviewers:
            <ul className="mt-1 space-y-0.5">
              {(focused?.reviewers || []).map((r, i) => (
                <li key={i} className="font-mono text-slate-700 dark:text-slate-200">
                  {r.email || r.role || 'unassigned'} <span className="text-slate-500">· SLA {r.sla_days}d</span>
                </li>
              ))}
            </ul>
          </div>
          {focusedState.approvals?.length > 0 && (
            <div className="mb-2">
              <div className="text-[10px] uppercase text-slate-500">Recorded approvals ({focusedState.approvals.length})</div>
              {focusedState.approvals.map((a, i) => (<div key={i} className="text-[10px]">✓ {a.reviewer} — {a.action} {a.comments && <span className="text-slate-500">&ldquo;{a.comments}&rdquo;</span>}</div>))}
            </div>
          )}
          {isCurrent && (focused?.allowed_actions || []).length > 0 && (
            <>
              <div className="flex items-center gap-2 mb-2">
                <Select value={actForm.action} onValueChange={(v) => setActForm({ ...actForm, action: v })}>
                  <SelectTrigger className="h-8 flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{focused.allowed_actions.map(a => <SelectItem key={a} value={a}>{a.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                </Select>
                <Button className="bg-[#1E4E8C] text-white h-8" onClick={act} data-testid="wf-act-submit"><Send className="w-3.5 h-3.5 mr-1" /> Act</Button>
              </div>
              <Textarea rows={2} placeholder="Comments (optional)" value={actForm.comments} onChange={(e) => setActForm({ ...actForm, comments: e.target.value })} data-testid="wf-act-comments" />
            </>
          )}
          {!isCurrent && focusStage !== detail.current_stage_index && (
            <div className="text-[10px] text-slate-500 italic">This stage is not the current one — action buttons are hidden.</div>
          )}
        </div>

        <div>
          <div className="text-[10px] uppercase text-slate-500 mb-1">History ({detail.history.length})</div>
          <div className="max-h-48 overflow-auto space-y-1">
            {detail.history.slice().reverse().map((h, i) => (
              <div key={i} className={`${subPanel} p-1.5 text-[10px]`}>
                <span className="text-slate-500">{new Date(h.at).toLocaleString()}</span>
                <span className="mx-1">·</span>
                {h.event === 'action' ? <><b className="text-sky-500">{h.action}</b> at stage {h.stage} by {h.actor}</> : <b className="text-emerald-500">{h.event}</b>}
                {h.comments && <span className="text-slate-500 ml-1">&ldquo;{h.comments}&rdquo;</span>}
                {h.forward_to && <span className="text-fuchsia-500 ml-1">→ {h.forward_to}</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
